import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowUpRight, Upload, X, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import { useLanguage } from "@/i18n/LanguageContext";
import Seo from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
 { key: "refrigeration-industrielle", fr: "Réfrigération industrielle", en: "Industrial refrigeration", pt: "Refrigeração industrial" },
 { key: "chambre-froide", fr: "Chambre froide", en: "Cold room", pt: "Câmara fria" },
 { key: "climatisation", fr: "Climatisation professionnelle", en: "Professional air conditioning", pt: "Climatização profissional" },
 { key: "energies-renouvelables", fr: "Énergies renouvelables", en: "Renewable energy", pt: "Energias renováveis" },
 { key: "produits-consommables", fr: "Produits & consommables professionnels", en: "Professional supplies & consumables", pt: "Produtos e consumíveis profissionais" },
 { key: "matieres-premieres", fr: "Matières premières naturelles", en: "Natural raw materials", pt: "Matérias-primas naturais" },
 { key: "filiere-cajou", fr: "Filière cajou", en: "Cashew value chain", pt: "Fileira do caju" },
 { key: "conseil", fr: "Conseil en implantation", en: "Establishment consulting", pt: "Consultoria de implantação" },
 { key: "autre", fr: "Autre demande", en: "Other request", pt: "Outro pedido" },
];

const BUDGETS = [
 { key: "undefined", fr: "Budget non défini", en: "Undefined", pt: "Orçamento não definido" },
 { key: "lt-1m", fr: "< 1 000 000 FCFA", en: "< 1 000 000 XOF", pt: "< 1 000 000 XOF" },
 { key: "1-5m", fr: "1 000 000 – 5 000 000 FCFA", en: "1M – 5M XOF", pt: "1M – 5M XOF" },
 { key: "5-15m", fr: "5 000 000 – 15 000 000 FCFA", en: "5M – 15M XOF", pt: "5M – 15M XOF" },
 { key: "15-50m", fr: "15 000 000 – 50 000 000 FCFA", en: "15M – 50M XOF", pt: "15M – 50M XOF" },
 { key: "gt-50m", fr: "> 50 000 000 FCFA", en: "> 50M XOF", pt: "> 50M XOF" },
 { key: "study", fr: "Sur étude", en: "On study basis", pt: "Sob estudo" },
];

const ALLOWED_EXTS = [".pdf", ".jpg", ".jpeg", ".png", ".webp", ".doc", ".docx", ".xls", ".xlsx"];
const MAX_FILE = 10 * 1024 * 1024;
const MAX_TOTAL = 30 * 1024 * 1024;
const MAX_FILES = 5;

const CATEGORY_URL_MAP = {
 refrigeration: "refrigeration-industrielle",
 "refrigeration-industrielle": "refrigeration-industrielle",
 "chambre-froide": "chambre-froide",
 climatisation: "climatisation",
 solaire: "energies-renouvelables",
 energie: "energies-renouvelables",
 "energies-renouvelables": "energies-renouvelables",
 "produits-consommables": "produits-consommables",
 consommables: "produits-consommables",
 "matieres-premieres": "matieres-premieres",
 cajou: "filiere-cajou",
 "filiere-cajou": "filiere-cajou",
 conseil: "conseil",
 autre: "autre",
};

const Field = ({ label, required, error, help, children, testId }) => (
 <div className="flex flex-col gap-2" data-testid={testId}>
 <label className="label-overline text-nanda-ink">{label}{required && <span className="text-nanda-terracotta ml-1">*</span>}</label>
 {children}
 {help && <div className="text-xs text-nanda-muted italic">{help}</div>}
 {error && <div className="text-xs text-nanda-terracotta flex items-center gap-1.5"><AlertCircle size={12} strokeWidth={2} />{error}</div>}
 </div>
);
const inputCls = "w-full border border-nanda-line bg-white px-4 py-3 text-[15px] text-nanda-ink focus:outline-none focus:border-nanda-ink transition-colors";

export default function Devis() {
 const tt = useT();
 const { locale } = useLanguage();
 const [params] = useSearchParams();

 const paramCat = (params.get("category") || params.get("subject") || "").toLowerCase();
 const initialCategory = CATEGORY_URL_MAP[paramCat] || "";

 const [form, setForm] = useState({
 first_name: "", last_name: "", company: "", job_title: "",
 country: "", city: "", phone: "", email: "", website: "",
 category: initialCategory, sector: "",
 description: "", details: {},
 budget: "", deadline: "", start_date: "",
 preferred_contact: "", project_address: "", additional: "",
 consent: false, marketing_consent: false, website_hp: "",
 });
 const [files, setFiles] = useState([]);
 const [errors, setErrors] = useState({});
 const [submitting, setSubmitting] = useState(false);
 const [progress, setProgress] = useState(0);
 const [result, setResult] = useState(null);
 const [serverError, setServerError] = useState(null);

 const catLabel = (c) => c[locale] || c.fr;

 useEffect(() => { if (initialCategory) setForm((f) => ({...f, category: initialCategory })); }, [initialCategory]);

 const update = (k, v) => setForm((f) => ({...f, [k]: v }));

 const onFilesSelected = (e) => {
 const list = Array.from(e.target.files || []);
 const newFiles = [...files];
 let totalSize = files.reduce((s, f) => s + f.size, 0);
 for (const f of list) {
 if (newFiles.length >= MAX_FILES) { setErrors((er) => ({...er, files: tt(`Maximum ${MAX_FILES} fichiers.`, `Max ${MAX_FILES} files.`, `Máx. ${MAX_FILES} ficheiros.`) })); break; }
 const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
 if (!ALLOWED_EXTS.includes(ext)) { setErrors((er) => ({...er, files: tt(`Format non accepté : ${ext}`, `Unsupported format: ${ext}`, `Formato não aceite: ${ext}`) })); continue; }
 if (f.size > MAX_FILE) { setErrors((er) => ({...er, files: tt(`« ${f.name} » dépasse 10 Mo.`, `"${f.name}" exceeds 10 MB.`, `« ${f.name} » excede 10 MB.`) })); continue; }
 if (totalSize + f.size > MAX_TOTAL) { setErrors((er) => ({...er, files: tt("Total > 30 Mo.", "Total > 30 MB.", "Total > 30 MB.") })); break; }
 totalSize += f.size;
 newFiles.push(f);
 }
 setFiles(newFiles);
 e.target.value = "";
 };
 const removeFile = (i) => setFiles((fs) => fs.filter((_, idx) => idx !== i));

 const validate = () => {
 const er = {};
 if (!form.first_name.trim()) er.first_name = tt("Requis", "Required", "Obrigatório");
 if (!form.last_name.trim()) er.last_name = tt("Requis", "Required", "Obrigatório");
 if (!form.company.trim()) er.company = tt("Requis", "Required", "Obrigatório");
 if (!form.country.trim()) er.country = tt("Requis", "Required", "Obrigatório");
 if (!form.phone.trim()) er.phone = tt("Requis", "Required", "Obrigatório");
 if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = tt("E-mail invalide", "Invalid email", "E-mail inválido");
 if (!form.category) er.category = tt("Sélectionnez une catégorie", "Select a category", "Seleccione uma categoria");
 if (!form.description.trim() || form.description.trim().length < 10) er.description = tt("Décrivez votre besoin (min. 10 caractères)", "Describe your need (min. 10 chars)", "Descreva a sua necessidade (mín. 10 caracteres)");
 if (!form.deadline.trim()) er.deadline = tt("Requis", "Required", "Obrigatório");
 if (!form.consent) er.consent = tt("Consentement requis", "Consent required", "Consentimento obrigatório");
 setErrors(er);
 return Object.keys(er).length === 0;
 };

 const submit = async (e) => {
 e.preventDefault;
 setServerError(null);
 if (!validate) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
 setSubmitting(true); setProgress(5);

 const fd = new FormData;
 Object.entries(form).forEach(([k, v]) => {
 if (k === "details") fd.append(k, JSON.stringify(v || {}));
 else if (typeof v === "boolean") fd.append(k, v ? "1" : "");
 else if (v !== null && v !== undefined) fd.append(k, String(v));
 });
 fd.append("source_page", typeof window !== "undefined" ? window.location.pathname : "");
 files.forEach((f) => fd.append("files", f, f.name));
 fd.append("locale", locale);

 const xhr = new XMLHttpRequest;
 xhr.open("POST", `${API}/api/quote-requests`);
 xhr.upload.onprogress = (evt) => { if (evt.lengthComputable) setProgress(Math.round((evt.loaded / evt.total) * 90)); };
 xhr.onload = () => {
 setProgress(100); setSubmitting(false);
 if (xhr.status >= 200 && xhr.status < 300) {
 try { setResult(JSON.parse(xhr.responseText)); } catch { setResult({ ok: true, reference: "—" }); }
 window.scrollTo({ top: 0, behavior: "smooth" });
 } else {
 let msg = tt("Erreur d'envoi. Veuillez réessayer.", "Submission error. Please try again.", "Erro de envio. Tente novamente.");
 try { const j = JSON.parse(xhr.responseText); if (j?.detail) msg = j.detail; } catch { /* ignore */ }
 setServerError(msg);
 }
 };
 xhr.onerror = () => { setSubmitting(false); setServerError(tt("Erreur réseau.", "Network error.", "Erro de rede.")); };
 xhr.send(fd);
 };

 if (result?.ok) {
 return (
 <div data-testid="page-devis-success" className="min-h-screen bg-nanda-bg">
 <Seo />
 <section className="mx-auto max-w-3xl px-6 md:px-10 lg:px-14 py-32 md:py-40">
 <FadeUp>
 <CheckCircle2 size={56} strokeWidth={1.2} className="text-nanda-terracotta mb-8" />
 <div className="label-overline text-nanda-muted mb-4">{tt("Demande reçue", "Request received", "Pedido recebido")}</div>
 <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-nanda-ink">
 {tt("Merci, votre demande est bien enregistrée.", "Thank you, your request has been received.", "Obrigado, o seu pedido foi recebido.")}
 </h1>
 <div className="mt-10 border-l-2 border-nanda-terracotta pl-6 py-2">
 <div className="label-overline text-nanda-muted">{tt("Numéro de référence", "Reference number", "Número de referência")}</div>
 <div className="font-serif text-3xl md:text-4xl text-nanda-ink mt-2 tabular-nums" data-testid="devis-reference">{result.reference}</div>
 <p className="mt-4 text-sm text-nanda-inkSoft">
 {tt("Conservez cette référence pour tout échange futur.", "Please keep this reference for future exchanges.", "Guarde esta referência para futuros contactos.")}
 </p>
 </div>
 <p className="mt-10 text-nanda-inkSoft text-[17px] leading-relaxed">
 {tt("Un membre de notre équipe reviendra vers vous dans les meilleurs délais.", "A member of our team will get back to you shortly.", "Um membro da nossa equipa entrará em contacto brevemente.")}
 </p>
 <div className="mt-12">
 <Link to="/" className="inline-flex items-center gap-3 border border-nanda-line text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-bgAlt transition-colors duration-500" data-testid="devis-back-home">
 {tt("Retour à l'accueil", "Back to home", "Voltar ao início")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </section>
 </div>
 );
 }

 const currentCat = CATEGORIES.find((c) => c.key === form.category);
 const showRefrigDetails = ["refrigeration-industrielle", "chambre-froide"].includes(form.category);
 const showClimDetails = form.category === "climatisation";
 const showEnergyDetails = form.category === "energies-renouvelables";
 const showMpDetails = ["matieres-premieres", "filiere-cajou"].includes(form.category);

 return (
 <div data-testid="page-devis" className="bg-nanda-bg">
 <Seo />
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-14 pt-24 md:pt-36 pb-12 md:pb-16">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-6">{tt("Demande de devis B2B", "B2B quote request", "Pedido de orçamento B2B")}</div>
 <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] text-nanda-ink">
 {tt("Décrivez votre projet.", "Describe your project.", "Descreva o seu projecto.")}<br />
 <span className="text-nanda-terracotta italic">{tt("Nous revenons vers vous.", "We come back to you.", "Voltaremos ao seu contacto.")}</span>
 </h1>
 <p className="mt-8 max-w-2xl text-nanda-inkSoft text-lg leading-relaxed">
 {tt("Ce formulaire vous permet de transmettre en toute sécurité les informations et documents nécessaires pour une réponse commerciale ou technique adaptée.",
 "This form lets you securely send the information and documents needed for a tailored commercial or technical response.",
 "Este formulário permite-lhe transmitir com segurança as informações e documentos necessários para uma resposta comercial ou técnica adaptada.")}
 </p>
 </FadeUp>
 </div>
 </section>

 <form onSubmit={submit} data-testid="devis-form" className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-14 py-14 md:py-20 space-y-14">
 {serverError && (
 <div className="border border-nanda-terracotta bg-nanda-terracotta/5 p-5 flex items-start gap-3" data-testid="devis-server-error">
 <AlertCircle size={20} strokeWidth={1.5} className="text-nanda-terracotta shrink-0 mt-0.5" />
 <div className="text-sm text-nanda-ink">{serverError}</div>
 </div>
 )}

 <input type="text" name="website_hp" autoComplete="off" tabIndex="-1" aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }} value={form.website_hp} onChange={(e) => update("website_hp", e.target.value)} />

 <div>
 <div className="label-overline text-nanda-terracotta mb-2">01</div>
 <h2 className="font-serif text-3xl md:text-4xl mb-8 text-nanda-ink">{tt("Votre organisation", "Your organisation", "A sua organização")}</h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 <Field label={tt("Prénom", "First name", "Nome próprio")} required error={errors.first_name} testId="devis-first_name"><input className={inputCls} value={form.first_name} onChange={(e) => update("first_name", e.target.value)} maxLength={80} /></Field>
 <Field label={tt("Nom", "Last name", "Apelido")} required error={errors.last_name} testId="devis-last_name"><input className={inputCls} value={form.last_name} onChange={(e) => update("last_name", e.target.value)} maxLength={80} /></Field>
 <Field label={tt("Entreprise ou organisation", "Company or organisation", "Empresa ou organização")} required error={errors.company} testId="devis-company"><input className={inputCls} value={form.company} onChange={(e) => update("company", e.target.value)} maxLength={200} /></Field>
 <Field label={tt("Fonction", "Job title", "Função")} testId="devis-job_title"><input className={inputCls} value={form.job_title} onChange={(e) => update("job_title", e.target.value)} maxLength={120} /></Field>
 <Field label={tt("Pays", "Country", "País")} required error={errors.country} testId="devis-country"><input className={inputCls} value={form.country} onChange={(e) => update("country", e.target.value)} maxLength={80} /></Field>
 <Field label={tt("Ville ou zone du projet", "City or project area", "Cidade ou zona do projecto")} testId="devis-city"><input className={inputCls} value={form.city} onChange={(e) => update("city", e.target.value)} maxLength={120} /></Field>
 <Field label={tt("Téléphone (indicatif inclus)", "Phone (with country code)", "Telefone (com indicativo)")} required error={errors.phone} testId="devis-phone"><input type="tel" className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+245... · +221..." maxLength={40} /></Field>
 <Field label={tt("Adresse e-mail", "E-mail address", "Endereço de e-mail")} required error={errors.email} testId="devis-email"><input type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} maxLength={200} /></Field>
 <Field label={tt("Site internet", "Website", "Site")} testId="devis-website"><input type="url" className={inputCls} value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://..." maxLength={255} /></Field>
 <Field label={tt("Secteur d'activité", "Activity sector", "Sector de actividade")} testId="devis-sector"><input className={inputCls} value={form.sector} onChange={(e) => update("sector", e.target.value)} maxLength={120} /></Field>
 </div>
 </div>

 <div>
 <div className="label-overline text-nanda-terracotta mb-2">02</div>
 <h2 className="font-serif text-3xl md:text-4xl mb-8 text-nanda-ink">{tt("Votre projet", "Your project", "O seu projecto")}</h2>
 <div className="grid grid-cols-1 gap-5">
 <Field label={tt("Type de demande", "Request type", "Tipo de pedido")} required error={errors.category} testId="devis-category">
 <select className={inputCls} value={form.category} onChange={(e) => update("category", e.target.value)}>
 <option value="">{tt("Sélectionner", "Select", "Seleccionar")}</option>
 {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{catLabel(c)}</option>)}
 </select>
 </Field>

 {showRefrigDetails && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-nanda-bgAlt p-6 border border-nanda-line" data-testid="devis-details-refri">
 <Field label={tt("Température souhaitée", "Target temperature", "Temperatura pretendida")}><input className={inputCls} placeholder="+2 °C · -18 °C..." onChange={(e) => update("details", {...form.details, temperature: e.target.value })} /></Field>
 <Field label={tt("Capacité / volume (m³, tonnes)", "Capacity / volume", "Capacidade / volume")}><input className={inputCls} onChange={(e) => update("details", {...form.details, capacity: e.target.value })} /></Field>
 <Field label={tt("Produits à conserver", "Products to preserve", "Produtos a conservar")}><input className={inputCls} onChange={(e) => update("details", {...form.details, products: e.target.value })} /></Field>
 <Field label={tt("Dimensions disponibles", "Available dimensions", "Dimensões disponíveis")}><input className={inputCls} onChange={(e) => update("details", {...form.details, dimensions: e.target.value })} /></Field>
 </div>
 )}
 {showClimDetails && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-nanda-bgAlt p-6 border border-nanda-line" data-testid="devis-details-clim">
 <Field label={tt("Type de bâtiment", "Building type", "Tipo de edifício")}><input className={inputCls} onChange={(e) => update("details", {...form.details, building: e.target.value })} /></Field>
 <Field label={tt("Surface approximative (m²)", "Approximate area (m²)", "Área aproximada (m²)")}><input className={inputCls} onChange={(e) => update("details", {...form.details, area: e.target.value })} /></Field>
 <Field label={tt("Nombre de pièces / zones", "Number of rooms / zones", "N.º de divisões / zonas")}><input className={inputCls} onChange={(e) => update("details", {...form.details, rooms: e.target.value })} /></Field>
 </div>
 )}
 {showEnergyDetails && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-nanda-bgAlt p-6 border border-nanda-line" data-testid="devis-details-energy">
 <Field label={tt("Consommation estimée (kWh/mois)", "Estimated consumption (kWh/month)", "Consumo estimado (kWh/mês)")}><input className={inputCls} onChange={(e) => update("details", {...form.details, consumption: e.target.value })} /></Field>
 <Field label={tt("Puissance souhaitée (kWc)", "Desired power (kWp)", "Potência pretendida (kWp)")}><input className={inputCls} onChange={(e) => update("details", {...form.details, power: e.target.value })} /></Field>
 <Field label={tt("Présence du réseau électrique", "Grid availability", "Disponibilidade da rede")}>
 <select className={inputCls} onChange={(e) => update("details", {...form.details, grid: e.target.value })}>
 <option value="">—</option><option value="yes">{tt("Oui", "Yes", "Sim")}</option><option value="partial">{tt("Partielle", "Partial", "Parcial")}</option><option value="no">{tt("Non", "No", "Não")}</option>
 </select>
 </Field>
 <Field label={tt("Besoin de batteries ?", "Battery storage needed?", "Precisa de baterias?")}>
 <select className={inputCls} onChange={(e) => update("details", {...form.details, battery: e.target.value })}>
 <option value="">—</option><option value="yes">{tt("Oui", "Yes", "Sim")}</option><option value="no">{tt("Non", "No", "Não")}</option><option value="tbd">{tt("À évaluer", "To assess", "A avaliar")}</option>
 </select>
 </Field>
 </div>
 )}
 {showMpDetails && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-nanda-bgAlt p-6 border border-nanda-line" data-testid="devis-details-mp">
 <Field label={tt("Produit recherché", "Product sought", "Produto procurado")}><input className={inputCls} onChange={(e) => update("details", {...form.details, product: e.target.value })} /></Field>
 <Field label={tt("Quantité / conditionnement", "Quantity / packaging", "Quantidade / embalagem")}><input className={inputCls} onChange={(e) => update("details", {...form.details, quantity: e.target.value })} /></Field>
 <Field label={tt("Fréquence d'achat", "Purchase frequency", "Frequência de compra")}><input className={inputCls} onChange={(e) => update("details", {...form.details, frequency: e.target.value })} /></Field>
 </div>
 )}

 <Field label={tt("Description détaillée du besoin", "Detailed description of the need", "Descrição detalhada da necessidade")} required error={errors.description} testId="devis-description">
 <textarea rows={6} className={inputCls} value={form.description} onChange={(e) => update("description", e.target.value)} maxLength={4000} />
 </Field>
 </div>
 </div>

 <div>
 <div className="label-overline text-nanda-terracotta mb-2">03</div>
 <h2 className="font-serif text-3xl md:text-4xl mb-8 text-nanda-ink">{tt("Budget & calendrier", "Budget & timeline", "Orçamento e calendário")}</h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 <Field label={tt("Budget indicatif", "Indicative budget", "Orçamento indicativo")} testId="devis-budget">
 <select className={inputCls} value={form.budget} onChange={(e) => update("budget", e.target.value)}>
 <option value="">{tt("Non précisé", "Not specified", "Não especificado")}</option>
 {BUDGETS.map((b) => <option key={b.key} value={b.key}>{b[locale] || b.fr}</option>)}
 </select>
 </Field>
 <Field label={tt("Délai souhaité", "Desired timeline", "Prazo desejado")} required error={errors.deadline} testId="devis-deadline"><input className={inputCls} value={form.deadline} onChange={(e) => update("deadline", e.target.value)} placeholder={tt("Ex. sous 3 mois", "e.g. within 3 months", "ex. em 3 meses")} maxLength={200} /></Field>
 <Field label={tt("Date souhaitée de démarrage", "Preferred start date", "Data preferida de arranque")} testId="devis-start_date"><input type="date" className={inputCls} value={form.start_date} onChange={(e) => update("start_date", e.target.value)} /></Field>
 <Field label={tt("Mode de contact préféré", "Preferred contact method", "Modo de contacto preferido")} testId="devis-preferred_contact">
 <select className={inputCls} value={form.preferred_contact} onChange={(e) => update("preferred_contact", e.target.value)}>
 <option value="">—</option><option value="email">E-mail</option><option value="phone">{tt("Téléphone", "Phone", "Telefone")}</option><option value="whatsapp">WhatsApp</option>
 </select>
 </Field>
 <Field label={tt("Adresse du projet", "Project address", "Morada do projecto")} testId="devis-project_address"><input className={inputCls} value={form.project_address} onChange={(e) => update("project_address", e.target.value)} maxLength={255} /></Field>
 <Field label={tt("Informations complémentaires", "Additional information", "Informações complementares")} testId="devis-additional"><input className={inputCls} value={form.additional} onChange={(e) => update("additional", e.target.value)} maxLength={2000} /></Field>
 </div>
 </div>

 <div>
 <div className="label-overline text-nanda-terracotta mb-2">04</div>
 <h2 className="font-serif text-3xl md:text-4xl mb-3 text-nanda-ink">{tt("Documents (facultatif)", "Documents (optional)", "Documentos (opcional)")}</h2>
 <p className="text-sm text-nanda-inkSoft mb-6 max-w-2xl">
 {tt("Plans, cahiers des charges, photos de chantier, factures d'électricité, fiches techniques… PDF, JPG, PNG, WEBP, DOC, DOCX, XLS, XLSX. Max. 5 fichiers, 10 Mo chacun, 30 Mo au total.",
 "Plans, specifications, site photos, electricity bills, technical sheets... PDF, JPG, PNG, WEBP, DOC, DOCX, XLS, XLSX. Max 5 files, 10 MB each, 30 MB total.",
 "Plantas, cadernos de encargos, fotografias, facturas de electricidade, fichas técnicas... PDF, JPG, PNG, WEBP, DOC, DOCX, XLS, XLSX. Máx. 5 ficheiros, 10 MB cada, 30 MB total.")}
 </p>
 <label htmlFor="devis-files" className="block cursor-pointer border-2 border-dashed border-nanda-line hover:border-nanda-ink bg-white p-8 text-center transition-colors" data-testid="devis-files-drop">
 <Upload size={28} strokeWidth={1.2} className="mx-auto text-nanda-terracotta mb-3" />
 <div className="font-serif text-lg text-nanda-ink">{tt("Ajouter des fichiers", "Add files", "Adicionar ficheiros")}</div>
 <div className="text-xs text-nanda-muted mt-1">{ALLOWED_EXTS.join(" · ")}</div>
 <input id="devis-files" type="file" multiple accept={ALLOWED_EXTS.join(",")} onChange={onFilesSelected} className="hidden" data-testid="devis-files-input" />
 </label>
 {errors.files && <div className="mt-3 text-xs text-nanda-terracotta flex items-center gap-1.5"><AlertCircle size={12} strokeWidth={2} />{errors.files}</div>}
 {files.length > 0 && (
 <ul className="mt-5 space-y-2" data-testid="devis-files-list">
 {files.map((f, i) => (
 <li key={i} className="flex items-center justify-between bg-white border border-nanda-line px-4 py-3 text-sm">
 <span className="truncate text-nanda-ink">{f.name}<span className="text-nanda-muted ml-2 tabular-nums">{(f.size / 1024 / 1024).toFixed(2)} MB</span></span>
 <button type="button" onClick={() => removeFile(i)} className="text-nanda-muted hover:text-nanda-terracotta" aria-label="remove"><X size={16} /></button>
 </li>
 ))}
 </ul>
 )}
 </div>

 <div>
 <div className="label-overline text-nanda-terracotta mb-2">05</div>
 <h2 className="font-serif text-3xl md:text-4xl mb-6 text-nanda-ink">{tt("Vérification & envoi", "Review & submit", "Verificação e envio")}</h2>
 <div className="bg-nanda-bgAlt border border-nanda-line p-6 text-sm text-nanda-inkSoft leading-relaxed">
 {tt("Les informations et documents transmis sont utilisés uniquement pour étudier votre demande et préparer une réponse commerciale ou technique. Ils ne sont pas accessibles publiquement.",
 "The information and documents provided are used solely to review your request and prepare a commercial or technical response. They are not publicly accessible.",
 "As informações e documentos fornecidos são utilizados apenas para analisar o seu pedido e preparar uma resposta comercial ou técnica. Não são acessíveis publicamente.")}
 </div>
 <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="devis-consent">
 <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} className="mt-1" />
 <span className="text-sm text-nanda-ink">
 {tt("J'accepte la politique de confidentialité et le traitement de ma demande par NANDA SÁ.", "I accept the privacy policy and the processing of my request by NANDA SÁ.", "Aceito a política de privacidade e o tratamento do meu pedido pela NANDA SÁ.")}
 <span className="text-nanda-terracotta">*</span>
 </span>
 </label>
 {errors.consent && <div className="mt-2 text-xs text-nanda-terracotta">{errors.consent}</div>}
 <label className="mt-3 flex items-start gap-3 cursor-pointer" data-testid="devis-marketing">
 <input type="checkbox" checked={form.marketing_consent} onChange={(e) => update("marketing_consent", e.target.checked)} className="mt-1" />
 <span className="text-sm text-nanda-inkSoft">
 {tt("J'accepte de recevoir des informations commerciales de NANDA SÁ (facultatif).", "I agree to receive commercial information from NANDA SÁ (optional).", "Aceito receber informações comerciais da NANDA SÁ (facultativo).")}
 </span>
 </label>
 </div>

 {submitting && (
 <div className="border border-nanda-line bg-white p-5" data-testid="devis-progress">
 <div className="flex items-center gap-3 mb-2 text-sm text-nanda-ink">
 <Loader2 size={16} className="animate-spin text-nanda-terracotta" />
 {tt("Envoi en cours…", "Uploading…", "A enviar…")} <span className="tabular-nums ml-auto">{progress}%</span>
 </div>
 <div className="h-1 bg-nanda-line overflow-hidden"><div className="h-full bg-nanda-terracotta transition-all" style={{ width: `${progress}%` }} /></div>
 </div>
 )}

 <div className="flex flex-wrap items-center gap-4 pt-4">
 <button type="submit" disabled={submitting} className="inline-flex items-center gap-3 bg-nanda-terracotta text-white px-10 py-5 label-overline hover:bg-nanda-terracotta transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="devis-submit">
 {submitting ? tt("Envoi…", "Sending…", "A enviar…") : tt("Envoyer la demande", "Submit the request", "Enviar o pedido")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </button>
 {currentCat && (<span className="text-xs text-nanda-muted label-overline">{tt("Catégorie", "Category", "Categoria")} : {catLabel(currentCat)}</span>)}
 </div>
 </form>
 </div>
 );
}
